import { useEffect, useMemo, useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { DocintConfigResponse, DocintDashboardResponse, DocintOverride } from "@/api/admin-api"

import {
  createDocintOverride,
  getDocintConfig,
  getDocintDashboard,
  refreshDocintConfig,
  revokeDocintOverride,
  updateDocintConfig,
} from "@/api/admin-api"
import { AlertTriangle, FileText, Plus, RefreshCw, Trash2, Unlock } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { useRawEntityQuery, useSaveRawEntityMutation } from "../../hooks/admin-queries"
import { CollapsibleManagementSection } from "../collapsible-management-section"
import { DelegationModeControl } from "../delegation-mode-control"
import { CreateOverrideDialog } from "./docint-dialogs"
import { RawJsonTab } from "../shared/raw-json-tab"

function formatSEK(value: number | null | undefined): string {
  if (value == null) return "-"
  return `${value.toFixed(2)} SEK`
}

const LIMIT_KEYS = [
  { key: "global_monthly_limit_sek", label: "Global Monthly" },
  { key: "global_daily_limit_sek", label: "Global Daily" },
  { key: "user_pool_monthly_limit_sek", label: "User Pool Monthly" },
  { key: "user_pool_daily_limit_sek", label: "User Pool Daily" },
  { key: "indexing_pool_monthly_limit_sek", label: "Indexing Pool Monthly" },
  { key: "indexing_pool_daily_limit_sek", label: "Indexing Pool Daily" },
  { key: "per_user_monthly_limit_sek", label: "Per-User Monthly" },
  { key: "per_user_daily_limit_sek", label: "Per-User Daily" },
] as const

type BlockedEntityInfo = {
  scopeType: "global" | "pool" | "user"
  scopeId?: string
  period: "daily" | "monthly"
  currentSpend?: number
}

export function DocintConfigPanel() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false)
  const [unlockEntity, setUnlockEntity] = useState<BlockedEntityInfo | undefined>(undefined)
  const [editingLimits, setEditingLimits] = useState<Record<string, string>>({})
  const [warningThreshold, setWarningThreshold] = useState<number>(80)

  const {
    data: config,
    isLoading: configLoading,
    refetch: refetchConfig,
  } = useQuery<DocintConfigResponse>({
    queryKey: ["admin", "docint", "config"],
    queryFn: () => getDocintConfig(),
    staleTime: 60000,
  })

  const { data: dashboard, isLoading: dashboardLoading } = useQuery<DocintDashboardResponse>({
    queryKey: ["admin", "docint", "dashboard"],
    queryFn: () => getDocintDashboard(),
    staleTime: 60000,
  })

  const updateConfigMutation = useMutation({
    mutationFn: updateDocintConfig,
    onSuccess: () => {
      toast.success(t("adminDashboard.docint.configUpdated", "Configuration updated"))
      setEditingLimits({})
      void queryClient.invalidateQueries({ queryKey: ["admin", "docint"] })
    },
    onError: () => {
      toast.error(t("adminDashboard.docint.configUpdateError", "Failed to update configuration"))
    },
  })

  const createOverrideMutation = useMutation({
    mutationFn: createDocintOverride,
    onSuccess: () => {
      toast.success(t("adminDashboard.docint.overrideCreated", "Override created"))
      void queryClient.invalidateQueries({ queryKey: ["admin", "docint"] })
      setOverrideDialogOpen(false)
    },
    onError: () => {
      toast.error(t("adminDashboard.docint.overrideError", "Failed to create override"))
    },
  })

  const revokeOverrideMutation = useMutation({
    mutationFn: ({ key, reason }: { key: string; reason?: string }) =>
      revokeDocintOverride(key, reason),
    onSuccess: () => {
      toast.success(t("adminDashboard.docint.overrideRevoked", "Override revoked"))
      void queryClient.invalidateQueries({ queryKey: ["admin", "docint"] })
    },
    onError: () => {
      toast.error(t("adminDashboard.docint.overrideRevokeError", "Failed to revoke override"))
    },
  })

  const refreshConfigMutation = useMutation({
    mutationFn: refreshDocintConfig,
    onSuccess: () => {
      toast.success(
        t("adminDashboard.docint.configReloaded", "Configuration reloaded from settings")
      )
      void queryClient.invalidateQueries({ queryKey: ["admin", "docint"] })
    },
    onError: () => {
      toast.error(t("adminDashboard.docint.configReloadError", "Failed to reload configuration"))
    },
  })

  const handleSaveLimits = () => {
    const updates: Record<string, number> = {}
    for (const [key, value] of Object.entries(editingLimits)) {
      const numValue = parseFloat(value)
      if (!isNaN(numValue) && numValue >= 0) {
        updates[key] = numValue
      }
    }
    if (Object.keys(updates).length > 0 || warningThreshold !== config?.warning_threshold_percent) {
      updateConfigMutation.mutate({
        limits: updates,
        warning_threshold_percent: warningThreshold,
      })
    }
  }

  const formIsDirty =
    Object.keys(editingLimits).length > 0 ||
    (config !== undefined && warningThreshold !== config.warning_threshold_percent)

  useEffect(() => {
    if (config && Object.keys(editingLimits).length === 0) {
      setWarningThreshold(config.warning_threshold_percent)
    }
  }, [config, editingLimits])

  const isLoading = configLoading || dashboardLoading

  // Raw JSON tab plumbing (singleton endpoint).
  const rawEndpoint = "/admin/docint/config/raw"
  const { data: rawData, refetch: refetchRaw } =
    useRawEntityQuery<Record<string, unknown>>(rawEndpoint)
  const rawSaveMutation = useSaveRawEntityMutation<Record<string, unknown>>({
    endpoint: rawEndpoint,
    invalidateOnSuccess: [["admin", "docint"]],
  })
  const rawStored = useMemo(() => rawData?.entity ?? {}, [rawData])
  const rawHash = rawData?.hash ?? ""
  const [rawResetSignal, setRawResetSignal] = useState(0)
  useEffect(() => {
    setRawResetSignal((n) => n + 1)
  }, [rawStored])

  const handleRawSave = async (next: unknown, reason: string) => {
    if (!rawHash) return
    const parsed = (next || {}) as Record<string, unknown>
    try {
      await rawSaveMutation.mutateAsync({
        entity: parsed,
        expectedHash: rawHash,
        reason,
        source: "raw",
      })
      void refetchRaw()
      toast.success(
        t("adminDashboard.docintEdit.toastSavedRaw", {
          defaultValue: "DocInt config saved (raw)",
        })
      )
    } catch (err) {
      toast.error(
        t("adminDashboard.docintEdit.toastRawSaveFailed", { defaultValue: "Raw save failed" }),
        { description: err instanceof Error ? err.message : undefined }
      )
      throw err
    }
  }

  const activeOverrides = dashboard?.active_overrides || []
  const blockedEntities = dashboard?.blocked_entities || []
  const warningEntities = dashboard?.warning_entities || []

  return (
    <>
      <CollapsibleManagementSection
        icon={<FileText className="size-5" />}
        id="docint"
        isDirty={formIsDirty}
        storageKey="admin-management-docint-expanded"
        title={t("adminDashboard.docint.configTitle", "Document Intelligence Quota")}
        headerActions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => refetchConfig()}>
              <RefreshCw className={`mr-2 size-4 ${isLoading ? "animate-spin" : ""}`} />
              {t("common.refresh", "Refresh")}
            </Button>
            <Button
              disabled={refreshConfigMutation.isPending}
              size="sm"
              variant="outline"
              title={t(
                "adminDashboard.docint.reloadConfigTooltip",
                "Reload configuration from settings without restart"
              )}
              onClick={() => refreshConfigMutation.mutate()}
            >
              {refreshConfigMutation.isPending ? (
                <RefreshCw className="mr-2 size-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 size-4" />
              )}
              {t("adminDashboard.docint.reloadConfig", "Reload Config")}
            </Button>
            <Button size="sm" variant="default" onClick={() => setOverrideDialogOpen(true)}>
              <Plus className="mr-2 size-4" />
              {t("adminDashboard.docint.addOverride", "Add Override")}
            </Button>
            <DelegationModeControl capability="docint" />
          </div>
        }
      >
        <Tabs defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">
              {t("adminDashboard.docintEdit.tabForm", { defaultValue: "Form" })}
            </TabsTrigger>
            <TabsTrigger
              className="text-destructive data-[state=active]:text-destructive"
              value="raw"
            >
              {t("adminDashboard.docintEdit.tabRaw", { defaultValue: "Raw JSON" })}
            </TabsTrigger>
          </TabsList>
          <TabsContent className="mt-4" value="form">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="text-muted-foreground size-6 animate-spin" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Blocked entities warning */}
                {blockedEntities.length > 0 && (
                  <div className="bg-destructive/15 border-destructive text-foreground rounded-lg border p-4">
                    <div className="text-destructive mb-3 flex items-center gap-2 font-medium">
                      <AlertTriangle className="size-4" />
                      {t("adminDashboard.docint.blockedEntitiesTitle", "Blocked Entities")}
                    </div>
                    <div className="space-y-2">
                      {blockedEntities.map((entity) => (
                        <div
                          key={`${entity.scope}-${entity.period}`}
                          className="bg-background/50 flex items-center justify-between rounded-md px-3 py-2"
                        >
                          <div>
                            <Badge className="mr-2" variant="destructive">
                              {entity.scope}
                            </Badge>
                            <span className="text-muted-foreground text-sm">
                              {entity.period} - {formatSEK(entity.current_spend_sek)} /{" "}
                              {formatSEK(entity.limit_sek)}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const [scopeType, scopeId] = entity.scope.includes(":")
                                ? entity.scope.split(":", 2)
                                : [entity.scope, undefined]
                              setUnlockEntity({
                                scopeType: scopeType as "global" | "pool" | "user",
                                scopeId,
                                period: entity.period as "daily" | "monthly",
                                currentSpend: entity.current_spend_sek,
                              })
                              setOverrideDialogOpen(true)
                            }}
                          >
                            <Unlock className="mr-1 size-3" />
                            {t("adminDashboard.docint.unlock", "Unlock")}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Warning entities notice */}
                {warningEntities.length > 0 && blockedEntities.length === 0 && (
                  <div className="bg-warning/15 border-warning text-foreground rounded-lg border p-4">
                    <div className="text-warning mb-3 flex items-center gap-2 font-medium">
                      <AlertTriangle className="size-4" />
                      {t("adminDashboard.docint.warningEntitiesTitle", "Approaching Quota Limits")}
                    </div>
                    <div className="space-y-2">
                      {warningEntities.slice(0, 10).map((entity, idx) => (
                        <div
                          key={`${entity.scope}-${entity.period}-${idx}`}
                          className="bg-background/50 flex items-center justify-between rounded-md px-3 py-2"
                        >
                          <div>
                            <Badge className="border-warning text-warning mr-2" variant="outline">
                              {entity.scope}
                            </Badge>
                            <span className="text-muted-foreground text-sm">
                              {entity.period} - {entity.percentage}% -{" "}
                              {formatSEK(entity.current_spend_sek)} / {formatSEK(entity.limit_sek)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Limits configuration */}
                <div className="space-y-4">
                  <h4 className="font-medium">
                    {t("adminDashboard.docint.limitsTitle", "Quota Limits")}
                  </h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    {LIMIT_KEYS.map(({ key, label }) => (
                      <div key={key} className="space-y-1">
                        <Label htmlFor={key}>{label} (SEK)</Label>
                        <Input
                          defaultValue={config?.limits[key] ?? 0}
                          id={key}
                          min={0}
                          step={0.01}
                          type="number"
                          onChange={(e) =>
                            setEditingLimits((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Warning threshold */}
                <div className="space-y-4">
                  <h4 className="font-medium">
                    {t("adminDashboard.docint.warningThreshold", "Warning Threshold")}
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t("adminDashboard.docint.alertAt", "Alert at")}
                      </span>
                      <span className="font-medium">{warningThreshold}%</span>
                    </div>
                    <Slider
                      max={100}
                      min={50}
                      step={5}
                      value={[warningThreshold]}
                      onValueChange={(values) => setWarningThreshold(values[0])}
                    />
                    <p className="text-muted-foreground text-xs">
                      {t(
                        "adminDashboard.docint.warningThresholdHelp",
                        "Send warning alerts when quota usage reaches this percentage"
                      )}
                    </p>
                  </div>
                </div>

                <Button disabled={updateConfigMutation.isPending} onClick={handleSaveLimits}>
                  {updateConfigMutation.isPending ? (
                    <RefreshCw className="mr-2 size-4 animate-spin" />
                  ) : null}
                  {t("common.saveChanges", "Save Changes")}
                </Button>

                {/* Active overrides */}
                {activeOverrides.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="font-medium">
                      {t("adminDashboard.docint.activeOverrides", "{{count}} active override(s)", {
                        count: activeOverrides.length,
                      })}
                    </h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("adminDashboard.docint.scope", "Scope")}</TableHead>
                          <TableHead>{t("adminDashboard.docint.period", "Period")}</TableHead>
                          <TableHead>{t("adminDashboard.docint.type", "Type")}</TableHead>
                          <TableHead>{t("adminDashboard.docint.value", "Value")}</TableHead>
                          <TableHead>{t("adminDashboard.docint.reason", "Reason")}</TableHead>
                          <TableHead className="w-20" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeOverrides.map((override: DocintOverride) => {
                          // Compute the override key for display and deletion
                          const scopeBase =
                            override.scope_type === "global"
                              ? "global"
                              : `${override.scope_type}:${override.scope_id}`
                          const overrideKey = `${scopeBase}:${override.period}`

                          return (
                            <TableRow key={overrideKey}>
                              <TableCell>
                                <Badge variant="outline">{scopeBase}</Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">{override.period}</Badge>
                              </TableCell>
                              <TableCell>{override.override_type}</TableCell>
                              <TableCell>{formatSEK(override.value_sek)}</TableCell>
                              <TableCell className="max-w-xs truncate" title={override.reason}>
                                {override.reason}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => {
                                    const reason = window.prompt(
                                      t(
                                        "adminDashboard.docint.revokeReason",
                                        "Why are you revoking this override?"
                                      )
                                    )
                                    if (reason !== null) {
                                      revokeOverrideMutation.mutate({
                                        key: overrideKey,
                                        reason: reason || undefined,
                                      })
                                    }
                                  }}
                                >
                                  <Trash2 className="text-destructive size-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
          <TabsContent className="mt-4" value="raw">
            <RawJsonTab
              confirmToken="docint"
              isPending={rawSaveMutation.isPending || !rawHash}
              resetSignal={rawResetSignal}
              value={rawStored}
              resourceLabel={t("adminDashboard.docintEdit.rawResourceLabel", {
                defaultValue: "DocInt config",
              })}
              onSave={handleRawSave}
            />
          </TabsContent>
        </Tabs>
      </CollapsibleManagementSection>

      {/* Create Override / Unlock Dialog */}
      <CreateOverrideDialog
        initialEntity={unlockEntity}
        isPending={createOverrideMutation.isPending}
        open={overrideDialogOpen}
        onSubmit={(data) => createOverrideMutation.mutate(data)}
        onOpenChange={(open) => {
          setOverrideDialogOpen(open)
          if (!open) {
            setUnlockEntity(undefined)
          }
        }}
      />
    </>
  )
}
